import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormioForm } from '@formio/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, filter, merge, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { TrimmedRequiredValidator } from '../../../../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../../../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../../utilities/snackbar.service';
import { SaveAsTemplateDialogStore } from './save-as-template-dialog.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    ReactiveFormsModule,
    TranslatePipe,
    MatInput,
    MatLabel,
    MatFormField,
    MatRadioButton,
    MatRadioGroup,
    MatDropdown,
  ],
  providers: [SaveAsTemplateDialogStore, HttpUtilitiesService],
  selector: 'app-save-as-template-dialog',
  templateUrl: './save-as-template-dialog.component.html',
})
export class SaveAsTemplateDialogComponent implements OnInit {
  public formGroup = new FormGroup({
    saveAsNewTemplateForm: new FormGroup({
      name: new FormControl<string>('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)]),
    }),
    saveIntoExistingTemplateForm: new FormGroup({
      existingTemplate: new FormControl<IDropdownOption[]>([], [Validators.required]),
    }),
    saveMode: new FormControl<'saveAsNew' | 'saveIntoExisting' | null>(null, [Validators.required]),
  });
  public readonly searchSubject = new BehaviorSubject<string>('');

  constructor(
    public readonly store: SaveAsTemplateDialogStore,
    private readonly dialogRef: MatDialogRef<SaveAsTemplateDialogComponent>,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public readonly data: { templateToSave: FormioForm },
  ) {}

  public ngOnInit(): void {
    this.formGroup.controls.saveMode.valueChanges
      .pipe(startWith(this.formGroup.controls.saveMode.value))
      .subscribe((saveMode) => {
        switch (saveMode) {
          case 'saveAsNew':
            this.formGroup.controls.saveAsNewTemplateForm.enable();
            this.formGroup.controls.saveIntoExistingTemplateForm.disable();
            break;
          case 'saveIntoExisting':
            this.formGroup.controls.saveIntoExistingTemplateForm.enable();
            this.formGroup.controls.saveAsNewTemplateForm.disable();
            break;
        }
      });

    merge(
      this.formGroup.controls.saveMode.valueChanges.pipe(
        startWith(this.formGroup.controls.saveMode.value),
        distinctUntilChanged(),
        filter((mode) => mode === 'saveIntoExisting'),
        map(() => ''),
      ),
      this.searchSubject,
    ).subscribe((search) => {
      this.store.getTemplateDropdownOptions({
        fields: ['name'],
        limit: 10,
        ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      });
    });

    merge(
      this.store.select((state) => state.createTemplateStatus).pipe(filter((status) => status === ELoadStatus.success)),
      this.store
        .select((state) => state.updateTemplateContentStatus)
        .pipe(filter((status) => status === ELoadStatus.success)),
    ).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.dialogRef.close();
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.formGroup.valid || !this.data.templateToSave) {
      return;
    }

    switch (this.formGroup.value.saveMode) {
      case 'saveAsNew':
        this.store.createOneTemplate({
          form: this.data.templateToSave,
          name: FormUtilitiesService.trimIfString(this.formGroup.value.saveAsNewTemplateForm!.name!),
        });
        break;
      case 'saveIntoExisting':
        this.store.updateTemplateContent({
          form: this.data.templateToSave,
          id: this.formGroup.value.saveIntoExistingTemplateForm!.existingTemplate![0].id,
        });
        break;
    }
  }
}
